import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Add auth token from memory (fallback for non-cookie scenarios)
api.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('_at');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const reqUrl = error.config?.url || '';
        const isAuthAttempt = reqUrl.includes('/auth/login') || reqUrl.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthAttempt) {
            sessionStorage.removeItem('_at');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
};

// Customer Details
export const customerApi = {
    getDetails: () => api.get('/customer-details'),
    saveDetails: (data) => api.post('/customer-details', data),
    getDetailsById: (id) => api.get(`/customer-details/${id}`),
    approve: (id) => api.put(`/customer-details/${id}/approve`),
};

// Documents
export const documentsApi = {
    getAll: () => api.get('/documents'),
    create: (data) => api.post('/documents', data),
    upload: (formData) => api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    download: (id) => api.get(`/documents/${id}/download`),
    delete: (id) => api.delete(`/documents/${id}`),
};

// Reports
export const reportsApi = {
    getAll: (type, customerId) => api.get('/reports', { params: { report_type: type, customer_id: customerId } }),
    getById: (id) => api.get(`/reports/${id}`),
    create: (data) => api.post('/reports', data),
    update: (id, data) => api.put(`/reports/${id}`, data),
    delete: (id) => api.delete(`/reports/${id}`),
    compare: (data) => api.post('/reports/compare', data),
    compareLinkedInProfiles: (data) => api.post('/reports/compare-linkedin-profiles', data),
    getLinkedInProfiles: () => api.get('/reports/linkedin-profiles'),
    importCsv: (formData) => api.post('/reports/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getCsvTemplate: () => api.get('/reports/export-csv-template'),
    generateAiInsights: (reportId) => api.post(`/reports/${reportId}/ai-insights`),
};

// ABM Journey
export const journeyApi = {
    get: () => api.get('/abm-journey'),
    getByCustomer: (customerId) => api.get(`/abm-journey/${customerId}`),
    save: (data) => api.post('/abm-journey', data),
    update: (data) => api.put('/abm-journey', data),
    completePhase: (journeyId, phaseId) => api.put(`/abm-journey/${journeyId}/phase/${phaseId}/complete`),
};

// Dashboard
export const dashboardApi = {
    getStats: () => api.get('/dashboard/stats'),
};

// Admin
export const adminApi = {
    getCustomers: () => api.get('/admin/customers'),
    createCustomer: (data) => api.post('/admin/customers', data),
    updateCustomer: (userId, data) => api.put(`/admin/customers/${userId}`, data),
    deleteCustomer: (id) => api.delete(`/admin/customers/${id}`),
    getOverallMetrics: (customerId) => api.get(`/admin/overall-metrics/${customerId}`),
    saveOverallMetrics: (data) => api.post('/admin/overall-metrics', data),
    updateOverallMetrics: (customerId, data) => api.put(`/admin/overall-metrics/${customerId}`, data),
};

// Seed Data
export const seedApi = {
    seedData: () => api.post('/seed-data'),
};

export default api;
